/**
 * Friendly copy for the structured `mfa_*` error codes returned by the
 * money-moving approve endpoints (deposits / withdrawals / reversals),
 * which now require a fresh TOTP step-up code in the approver's request.
 */
import { getApiErrorCode, getApiErrorMessage } from './apiError';

/** The step-up routes take 6 to 8 characters. */
export const MFA_MIN = 6;
export const MFA_MAX = 8;

/** Keeps only the digits of what was typed or pasted, no more than the routes accept. */
export function cleanMfaInput(raw: string): string {
  return raw.replace(/\D/g, '').slice(0, MFA_MAX);
}

export function isMfaCode(code: string): boolean {
  return code.length >= MFA_MIN && code.length <= MFA_MAX;
}

export function approveMfaErrorMessage(err: unknown): string {
  switch (getApiErrorCode(err)) {
    case 'mfa_not_enrolled':
      return 'Turn on two-factor authentication in your profile before approving.';
    case 'mfa_invalid_code':
      return 'That code did not match. Enter the current code from your authenticator.';
    case 'mfa_code_replayed':
      return 'That code was already used. Wait for the next one and try again.';
    case 'mfa_too_many_attempts':
      return 'Too many attempts. Please wait a moment before trying again.';
    default:
      return getApiErrorMessage(err, 'Could not approve.');
  }
}

/** A refused step-up has its own copy; anything else keeps the server's reason. A step-up confirms
 *  more than approvals (a request about one's data, a decision, a filing), so its words name no act. */
export function stepUpErrorMessage(err: unknown, fallback: string): string {
  if (getApiErrorCode(err) === 'mfa_not_enrolled') {
    return 'Turn on two-factor authentication in your profile first.';
  }
  return getApiErrorCode(err)?.startsWith('mfa_') === true
    ? approveMfaErrorMessage(err)
    : getApiErrorMessage(err, fallback);
}
